// src/store/chatStore.js
//
// KEY FIX: store `currentUserId` (the logged-in employee_id) in the store.
//
// ROOT CAUSE of all 3 bugs:
//   • Both DMs showed same name ("Bharathi Devalraju")
//   • Messages showed on wrong side (left instead of right)
//   • All caused by currentUserId being undefined in child components.
//
// When currentUserId is undefined:
//   resolveDisplayName: `id !== undefined` is always true →
//     picks the FIRST member every time → always same person
//   buildGroups isOwn: `senderId === undefined` always false →
//     every message renders as received (left side)
//
// FIX: ChatPage calls setCurrentUserId() on mount. All components read
// `s.currentUserId` directly from the store — no more prop drilling.

import { create } from "zustand";

export const EMPTY_ARRAY  = Object.freeze([]);
export const EMPTY_OBJECT = Object.freeze({});

export const toKey = (id) => {
  if (id === null || id === undefined) return id;
  const num = Number(id);
  return isNaN(num) ? id : num;
};

const useChatStore = create((set) => ({

  // ── Current logged-in user ─────────────────────────────────────────────────
  currentUserId:   null,   // employee_id string, e.g. "HYD123"
  currentUserName: null,   // full_name string

  setCurrentUser: (employeeId, fullName) =>
    set({ currentUserId: employeeId, currentUserName: fullName ?? null }),

  // ── E2EE Conversation Keys ────────────────────────────────────────────────
  conversationKeys: {}, // convId -> CryptoKey object

  setConversationKey: (convId, key) =>
    set((state) => ({
      conversationKeys: { ...state.conversationKeys, [toKey(convId)]: key },
    })),

  // ── Conversations ──────────────────────────────────────────────────────────
  conversations: [],

  setConversations: (convs) =>
    set((state) => {
      const seededUnreads = { ...state.unreadCounts };
      convs.forEach((c) => {
        if (c.unread_count !== undefined && seededUnreads[c.id] === undefined) {
          seededUnreads[c.id] = c.unread_count;
        }
      });
      return { conversations: convs, unreadCounts: seededUnreads };
    }),

  upsertConversation: (conv) =>
    set((state) => {
      const idx = state.conversations.findIndex((c) => c.id === conv.id);
      if (idx === -1) {
        return { conversations: [conv, ...state.conversations] };
      }
      const existing = state.conversations[idx];
      const merged = {
        ...existing,
        ...conv,
        members:
          conv.members && conv.members.length > 0
            ? conv.members
            : existing.members,
      };
      const without = state.conversations.filter((_, i) => i !== idx);
      return { conversations: [merged, ...without] };
    }),

  updateConversation: (convId, patch) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === convId ? { ...c, ...patch } : c
      ),
    })),

  updateConversationLastMessage: (convId, lastMsg) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === convId
          ? { ...c, last_message: lastMsg, updated_at: lastMsg.created_at }
          : c
      ),
    })),

  // ── Selected conversation ──────────────────────────────────────────────────
  selectedConvId: null,

  selectConversation: (convId) =>
    set((state) => {
      const id = toKey(convId);
      return {
        selectedConvId: id,
        unreadCounts: { ...state.unreadCounts, [id]: 0 },
      };
    }),

  // ── Messages ───────────────────────────────────────────────────────────────
  messages: {},

  setMessages: (convId, msgs, cursor, hasMore) =>
    set((state) => {
      const id = toKey(convId);
      return {
        messages: { ...state.messages, [id]: msgs },
        cursors:  { ...state.cursors,  [id]: cursor  ?? null },
        hasMore:  { ...state.hasMore,  [id]: hasMore ?? false },
      };
    }),

  prependMessages: (convId, msgs, cursor, hasMore) =>
    set((state) => {
      const id          = toKey(convId);
      const existing    = state.messages[id] ?? EMPTY_ARRAY;
      const existingIds = new Set(existing.map((m) => m.id));
      const fresh       = msgs.filter((m) => !existingIds.has(m.id));
      return {
        messages: { ...state.messages, [id]: [...fresh, ...existing] },
        cursors:  { ...state.cursors,  [id]: cursor  ?? null },
        hasMore:  { ...state.hasMore,  [id]: hasMore ?? false },
      };
    }),

  appendMessage: (convId, msg) =>
    set((state) => {
      const id       = toKey(convId);
      const existing = state.messages[id] ?? EMPTY_ARRAY;
      const dupIdx   = existing.findIndex(
        (m) =>
          (msg.id && String(m.id) === String(msg.id)) ||
          (msg.client_msg_id &&
            m.client_msg_id &&
            m.client_msg_id === msg.client_msg_id)
      );
      if (dupIdx !== -1) {
        const copy   = [...existing];
        copy[dupIdx] = {
          ...copy[dupIdx],
          ...msg,
          decryptedContent: msg.decryptedContent || copy[dupIdx].decryptedContent
        };
        return { messages: { ...state.messages, [id]: copy } };
      }
      return {
        messages: { ...state.messages, [id]: [...existing, msg] },
      };
    }),

  updateMessagesBulk: (convId, updates) =>
    set((state) => {
      const id = toKey(convId);
      const existing = state.messages[id] ?? EMPTY_ARRAY;
      if (existing.length === 0) return state;

      const updatesMap = new Map(updates.map(u => [String(u.id), u]));
      let changed = false;
      const newArr = existing.map(msg => {
        const update = updatesMap.get(String(msg.id));
        if (update) {
          changed = true;
          return { ...msg, ...update };
        }
        return msg;
      });

      if (!changed) return state;
      return { messages: { ...state.messages, [id]: newArr } };
    }),

  updateMessage: (convId, msgId, patch) =>
    set((state) => {
      const id = toKey(convId);
      return {
        messages: {
          ...state.messages,
          [id]: (state.messages[id] ?? EMPTY_ARRAY).map((m) => {
            if (String(m.id) === String(msgId)) {
              const updated = { ...m, ...patch };
              if (patch.ciphertext && patch.ciphertext !== m.ciphertext) {
                updated.decryptedContent = null;
              }
              return updated;
            }
            return m;
          }),
        },
      };
    }),

  deleteMessage: (convId, msgId, deleteType = "everyone") =>
    set((state) => {
      const id   = toKey(convId);
      const list = state.messages[id] ?? EMPTY_ARRAY;
      if (deleteType === "me") {
        return {
          messages: {
            ...state.messages,
            [id]: list.filter((m) => String(m.id ?? m.client_msg_id) !== String(msgId)),
          },
        };
      }
      return {
        messages: {
          ...state.messages,
          [id]: list.map((m) =>
            String(m.id ?? m.client_msg_id) === String(msgId)
              ? { ...m, is_deleted: true, content: "", ciphertext: "", iv: "" }
              : m
          ),
        },
      };
    }),

  // ── Cursors / pagination ───────────────────────────────────────────────────
  cursors: {},
  hasMore: {},

  // ── Unread counts ──────────────────────────────────────────────────────────
  unreadCounts: {},

  incrementUnread: (convId) =>
    set((state) => {
      const id = toKey(convId);
      return {
        unreadCounts: {
          ...state.unreadCounts,
          [id]: (state.unreadCounts[id] ?? 0) + 1,
        },
      };
    }),

  clearUnread: (convId) =>
    set((state) => {
      const id = toKey(convId);
      return {
        unreadCounts: { ...state.unreadCounts, [id]: 0 },
      };
    }),

  setUnreadCounts: (counts) =>
    set({ unreadCounts: counts }),

  // ── Typing users ───────────────────────────────────────────────────────────
  typingUsers: {},

  setTyping: (convId, userId, fullName) =>
    set((state) => {
      const id = toKey(convId);
      return {
        typingUsers: {
          ...state.typingUsers,
          [id]: {
            ...(state.typingUsers[id] ?? EMPTY_OBJECT),
            [userId]: fullName,
          },
        },
      };
    }),

  clearTyping: (convId, userId) =>
    set((state) => {
      const id       = toKey(convId);
      const existing = { ...(state.typingUsers[id] ?? EMPTY_OBJECT) };
      delete existing[userId];
      return { typingUsers: { ...state.typingUsers, [id]: existing } };
    }),

  // ── Presence ───────────────────────────────────────────────────────────────
  presence: {},

  setPresence: (employeeId, status) =>
    set((state) => ({
      presence: { ...state.presence, [employeeId]: status },
    })),

  bulkSetPresence: (presenceMap) =>
    set((state) => ({
      presence: { ...state.presence, ...presenceMap },
    })),

  // ── WebSocket state ────────────────────────────────────────────────────────
  wsConnected:    false,
  wsReconnecting: false,

  setWsConnected:    (v) => set({ wsConnected: v, wsReconnecting: false }),
  setWsReconnecting: (v) => set({ wsReconnecting: v, wsConnected: false }),

  // ── UI State ───────────────────────────────────────────────────────────────
  isMaximized: false,
  toggleIsMaximized: () => set((state) => ({ isMaximized: !state.isMaximized })),

  // ── Global send ────────────────────────────────────────────────────────────
  sendWebSocketMessage: null,
  setSendWebSocketMessage: (fn) => set({ sendWebSocketMessage: fn }),
  reset: () =>
    set({
      currentUserId: null,
      currentUserName: null,
      conversationKeys: {},
      conversations: [],
      selectedConvId: null,
      messages: {},
      cursors: {},
      hasMore: {},
      unreadCounts: {},
      typingUsers: {},
      presence: {},
      wsConnected: false,
      wsReconnecting: false,
      isMaximized: false,
      sendWebSocketMessage: null,
    }),
}));

export default useChatStore;